import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Box } from "@mui/material";
import { getAuth } from "firebase/auth";
import { getAuthCentral } from "../../config/FirebaseConfig";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { checkDB } from "../../models/DataFunctions.js";
import addLogDocument from "../addLogDocument"; // Custom log function
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken.js";
import JSZip from "jszip"; // For ZIP file handling
import Icon1 from "../../icons/Icon.js";
import SmallSpinner from "../../loading/SmallSpinner.js";
import { useSnackbar } from "notistack";
import { getDocDataByRef, getFlutterTime } from "../../models/DataFunctions.js";
import { translateDocumentId } from "../../pages/folder_pages/FolderV2.js";
import createDocApi from "../database/createDocApi.js";
import { addActivityLog } from "../../config/OnlineStatus.js";

const UploadModifThermal = ({
  folder,
  onUpdate,
  prefix,
  handleClickVariant,
}) => {
  const [files, setFiles] = useState([]);
  const [isHovered, setIsHovered] = useState(false);
  const [loading, setLoading] = useState(false);

  const onDropAccepted = useCallback(
    async (acceptedFiles) => {
      try {
        setLoading(true);
        const token = await getOrRefreshFirebaseToken();

        const auth = getAuthCentral();
        const currentUser = auth.currentUser;

        if (!currentUser) {
          console.error("User not authenticated");
          setLoading(false);
          return;
        }

        const db = getFirestore();
        const userRef = doc(db, "user", currentUser.uid);
        const userSnapshot = await getDoc(checkDB(userRef));

        if (!userSnapshot.exists()) {
          console.error("User document not found");
          setLoading(false);
          return;
        }

        const org_id = userSnapshot.get("org_id");
        let repo_name = await translateDocumentId("modif_thermal");
        await addActivityLog(
          "Documents déposés " + repo_name,
          folder.ref,
          org_id,
          userRef.id,
          "file_drop",
          prefix
        );

        const newFiles = [...files];

        // Process accepted files
        for (const file of acceptedFiles) {
          let processedFile = file;

          if (
            file.type === "application/zip" ||
            file.type === "application/x-zip-compressed" ||
            file.name.endsWith(".zip") ||
            file.name.endsWith(".xml")
          ) {
            handleClickVariant(
              "Le xml doit être déposé dans la partie XML",
              "error"
            );
            console.error("Le xml doit être déposé dans la partie XML");
            return;
          }

          newFiles.push(processedFile);

          // Upload the file
          const url = `${prefix}folders/${folder.id}/modif-thermal`;
          const formData = new FormData();
          formData.append("file", processedFile, processedFile.name);

          const headers = {
            Authorization: `Bearer ${token}`,
          };
          let start = new Date();

          const response = await fetch(url, {
            method: "POST",
            headers,
            body: formData,
          });

          const responseText = await response.text();
          let end = new Date();
          let elapsed = end - start;
          // Log the upload
          await addLogDocument(
            url,
            prefix,
            userRef,
            org_id,
            response.status.toString(),
            folder.id || "",
            "", // Assuming empty for docId
            "", // request body as string
            responseText,
            "POST",
            elapsed
          );

          if (!response.ok) {
            console.error(
              `Error response for file ${processedFile.name}: ${response.status}`
            );
          }
        }

        setFiles(newFiles); // Update state with all processed files
        onUpdate(); // Trigger callback to refresh data
      } catch (error) {
        console.error("Error uploading files:", error);
      } finally {
        setLoading(false);
      }
    },
    [files, folder, onUpdate, prefix]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropAccepted,
    accept: {
      "application/pdf": [],
    },
    maxFiles: Infinity,
    maxSize: 52428800,
  });

  return (
    <div className="flex flex-col gap-2 items-center w-full">
      <Box
        {...getRootProps()}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          border: "1px dashed",
          borderColor: isDragActive ? "primary.main" : "grey.400",
          borderRadius: "8px",
          height: "30px",
          textAlign: "center",
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          padding: "0px",
          width: "30px",
          cursor: "pointer",
          backgroundColor: loading
            ? "rgba(0, 0, 0, 0.1)"
            : isDragActive
              ? "rgba(0, 0, 255, 0.1)"
              : isHovered
                ? "rgba(0, 0, 0, 0.03)"
                : "transparent",
        }}
      >
        <input {...getInputProps()} />
        {loading ? (
          <SmallSpinner loading={true} />
        ) : (
          <Icon1 icon_name="join" size={15} fill="grey" />
        )}
      </Box>
    </div>
  );
};

export default UploadModifThermal;
